import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentRoutesService } from "../services/paymentRoutes.service";
import type { CorridorRouteCell, ProviderRailRoute, ProviderRouteKpis } from "../types/paymentRoutes.types";
import { buildCorridorCells, buildRouteKpis } from "../utils/routeMatrix";

export interface UseProviderRoutesResult {
  /** Every non-deleted route, active and inactive — the inactive ones decide the `BLOCKED` cells. */
  routes: ProviderRailRoute[];
  /** One row per corridor and direction, operational catalog first. */
  cells: CorridorRouteCell[];
  kpis: ProviderRouteKpis;

  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  /** A 401/403 latches for the session: the whole surface becomes a no-access view. */
  hasAccess: boolean;
  /** More routes exist than were loaded — must be disclosed, never silently capped. */
  truncated: boolean;
  loadedCount: number;
  totalCount: number;
  refresh: () => void;

  /** Corridor cell whose detail panel is open, kept in the URL so the view is shareable. */
  selectedKey: string | null;
  selectedCell: CorridorRouteCell | null;
  select: (key: string | null) => void;
}

/**
 * State of the provider-routes page.
 *
 * Freshness: **no polling.** Routes are configuration — they change only by human action, and there is no
 * websocket for payments. Data reloads on the manual button and after every successful create or delete.
 *
 * The whole catalog is loaded unfiltered (no `is_active`), because an inactive route still occupies its
 * corridor: filtering it out would render that corridor as "no route" and offer a create the unique index
 * refuses with `route_exists`.
 */
export function useProviderRoutes(): UseProviderRoutesResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const [routes, setRoutes] = useState<ProviderRailRoute[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Loading is DERIVED (an attempt was requested but has not settled) — no synchronous setState in the effect.
  const [settledKey, setSettledKey] = useState<number | null>(null);
  const isLoading = settledKey !== reloadKey && !hasLoadedOnce;

  useEffect(() => {
    let cancelled = false;
    PaymentRoutesService.listAll()
      .then((result) => {
        if (cancelled) return;
        setRoutes(result.routes);
        setTotalCount(result.total);
        setTruncated(result.truncated);
        setHasError(false);
        setHasLoadedOnce(true);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(error)) setHasAccess(false);
        else setHasError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setSettledKey(reloadKey);
        setIsRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    setReloadKey((key) => key + 1);
  }, []);

  const cells = useMemo(() => buildCorridorCells(routes), [routes]);
  const kpis = useMemo(() => buildRouteKpis(cells), [cells]);

  const selectedKey = searchParams.get("corridor");
  const selectedCell = useMemo(
    () => (selectedKey ? (cells.find((cell) => cell.key === selectedKey) ?? null) : null),
    [cells, selectedKey],
  );

  const select = useCallback(
    (key: string | null) => {
      // Guarded: react-router replaces the history entry even when the URL is byte-identical, and WebKit
      // counts every one against the same 100-per-10-seconds budget. Re-selecting the open corridor writes
      // nothing.
      const next = new URLSearchParams(searchParams);
      if (key) next.set("corridor", key);
      else next.delete("corridor");
      if (next.toString() === searchParams.toString()) return;

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return {
    routes,
    cells,
    kpis,
    isLoading,
    isRefreshing,
    hasError,
    hasAccess,
    truncated,
    loadedCount: routes.length,
    totalCount,
    refresh,
    selectedKey,
    selectedCell,
    select,
  };
}
